/**
 * Dati mancanti della scheda e stato, come si leggono (460, segnalazione di CIDIS del 19/10/2026).
 *
 * Che cosa manca lo decide il database (core.dati_mancanti) e arriva dall'API come codici: qui ci
 * sono soltanto i nomi da mostrare, nell'ordine in cui «Completa la scheda» li propone, e le parole
 * per gli stati della scheda.
 */

export type DatiMancanti = { obbligatori: string[]; consigliati: string[] }

/**
 * Nome del dato e perche' serve, per ogni codice di core.dati_mancanti. La categoria di utenti non
 * c'e' piu' (474, osservazioni n. 4 del 23/10/2026): si chiede nell'intervento, dove serve. Il modulo
 * firmato dei progetti (495) sta accanto ai consensi.
 */
export const DATI_MANCANTI: Record<string, { titolo: string; perche: string }> = {
  cognome: { titolo: 'Il cognome', perche: 'Senza il cognome la scheda non si riconosce.' },
  nome: { titolo: 'Il nome', perche: 'Senza il nome la scheda non si riconosce.' },
  sede_riferimento: { titolo: 'La Sede Servizio', perche: 'Decide quale sede segue la persona e chi vede la scheda.' },
  codice_fiscale: { titolo: 'Il codice fiscale', perche: 'Serve il codice fiscale, oppure il motivo per cui manca.' },
  nascita: { titolo: 'La data di nascita', perche: 'Va indicata la data, il solo anno, oppure che non è nota.' },
  fascia_eta: {
    titolo: 'La fascia d’età',
    perche: 'Senza data di nascita serve la fascia d’età: senza, gli interventi non si possono chiudere.',
  },
  livello_italiano: { titolo: 'Quanto conosce l’italiano', perche: 'È un dato obbligatorio per chi è assistito.' },
  appartenenza: {
    titolo: 'L’ente o la sede di appartenenza',
    perche: 'Per chi rappresenta un ente serve l’ente, oppure la sede CIDIS.',
  },
  nascita_da_verificare: {
    titolo: 'La data di nascita da controllare',
    perche: 'Viene dal vecchio archivio: va controllata su un documento.',
  },
  consensi: { titolo: 'I consensi 1 e 2', perche: 'Non sono ancora stati raccolti sul modulo firmato.' },
  firme_terzi: {
    titolo: 'Una firma da completare',
    perche: 'Ha firmato un’altra persona e manca il documento che ne prova il potere di firma.',
  },
  // 495: la seconda firma delle persone dei progetti con il modulo del destinatario (31/10/2026).
  modulo_progetto: {
    titolo: 'Il modulo firmato del progetto',
    perche: 'Ha un percorso aperto in un progetto con il suo modulo: oltre al modulo CIDIS va firmato anche quello.',
  },
  riconoscimento_titolo: { titolo: 'Il riconoscimento del titolo di studio', perche: 'Va detto se il titolo è riconosciuto in Italia.' },
  formazione_lavoro: { titolo: 'Formazione e lavoro', perche: 'La sezione è ancora vuota.' },
  condizione_abitativa: { titolo: 'La condizione abitativa e familiare', perche: 'La sezione è ancora vuota.' },
  servizi_bancari: { titolo: 'I servizi bancari e finanziari', perche: 'La sezione è ancora vuota.' },
}

/** Ordine dei codici: obbligatori, poi consigliati, come nell'API. */
export const ORDINE_DATI = Object.keys(DATI_MANCANTI)

export const titoloDato = (codice: string) => DATI_MANCANTI[codice]?.titolo ?? codice

/** «la data di nascita»: il nome del dato dentro una frase. */
export const datoInFrase = (codice: string) => {
  const t = titoloDato(codice)
  return t.charAt(0).toLowerCase() + t.slice(1)
}

/**
 * Stati della scheda in parole: «bozza» e «storico incompleto» non dicevano che cosa fare. Lo stato
 * lo calcola il database (460) dai dati obbligatori.
 */
export const STATI_SCHEDA: Record<string, { etichetta: string; aiuto: string }> = {
  completa: { etichetta: 'Completa', aiuto: 'Ha tutti i dati obbligatori.' },
  bozza: { etichetta: 'Da completare', aiuto: 'Creata qui: mancano dati obbligatori.' },
  storico_incompleto: {
    etichetta: 'Da completare (vecchio archivio)',
    aiuto: 'Viene dal vecchio archivio, che non chiedeva tutti i dati di oggi: mancano dati obbligatori.',
  },
  archiviata: { etichetta: 'Archiviata', aiuto: 'Non si segue più.' },
}

export const etichettaStatoScheda = (stato: string) => STATI_SCHEDA[stato]?.etichetta ?? stato.replace(/_/g, ' ')

/** Perche' una scheda e' da verificare, dai codici di core.persona_cause_verifica. */
export const CAUSE_VERIFICA: Record<string, string> = {
  nascita: 'la data di nascita non era valida: va controllata su un documento',
  codice_fiscale: 'il codice fiscale mancava o non era utilizzabile: va inserito, oppure va detto perché manca',
}

/** Schede da completare: stato bozza o storico incompleto. */
export const daCompletare = (stato: string) => stato === 'bozza' || stato === 'storico_incompleto'

// --- la scheda per chi la guarda (Osservazioni 4 del 23/10/2026) ---------------------------------

/** La parte della scheda letta che serve qui: permessi, maschera e dati mancanti. */
type SchedaPerMancanti = { mascherata: boolean; puo: { modificare: boolean }; dati_mancanti?: DatiMancanti }

/**
 * I dati mancanti da contare e da elencare. Senza nascita la fascia d'eta' si chiede nello stesso
 * passo di «Completa la scheda»: si conta una volta sola.
 */
export function mancantiDaMostrare(d: DatiMancanti | undefined): DatiMancanti {
  const tutti = d ?? { obbligatori: [], consigliati: [] }
  return {
    obbligatori: tutti.obbligatori.filter((c) => c !== 'fascia_eta' || !tutti.obbligatori.includes('nascita')),
    consigliati: tutti.consigliati,
  }
}

/**
 * Quanti dati mancano, obbligatori e consigliati detti per nome. La Direzione vedeva «Completa»
 * accanto a «Mancano 3 dati», e i tre erano consigliati: la frase ora lo dice. Nullo se non manca niente.
 */
export function riepilogoMancanti(d: DatiMancanti | undefined): string | null {
  const { obbligatori, consigliati } = mancantiDaMostrare(d)
  const o = obbligatori.length
  const c = consigliati.length
  if (!o && !c) return null
  if (o && c) {
    return `Mancano ${o === 1 ? 'un dato obbligatorio' : `${o} dati obbligatori`} e `
      + `${c === 1 ? 'uno consigliato' : `${c} consigliati`}.`
  }
  if (o) return o === 1 ? 'Manca un dato obbligatorio.' : `Mancano ${o} dati obbligatori.`
  return `${c === 1 ? 'Manca un dato consigliato' : `Mancano ${c} dati consigliati`}: `
    + 'la scheda ha tutti i dati obbligatori.'
}

/** Chi guarda la scheda la consulta soltanto: non la modifica, oppure la vede mascherata. */
export const soloConsultazione = (s: SchedaPerMancanti) => s.mascherata || !s.puo.modificare

/** Perche' non si modifica: la Direzione non lo capiva dalla pagina (Osservazioni 4). */
export const TESTO_SOLO_CONSULTAZIONE = 'Puoi solo consultare questa scheda: per modificarla serve un incarico '
  + 'di Operatore interno o di Project Manager sulla sua sede, sulla sua area o su un suo progetto.'

/**
 * Che cosa fa «Modifica la scheda» nell'intestazione: con dati obbligatori mancanti apre «Completa la
 * scheda», altrimenti porta alla linguetta Anagrafica con i dati anagrafici in modifica. Nullo per chi
 * non modifica: il comando non c'e'.
 */
export function azioneModifica(s: SchedaPerMancanti): 'completa' | 'anagrafica' | null {
  if (soloConsultazione(s)) return null
  return s.dati_mancanti?.obbligatori.length ? 'completa' : 'anagrafica'
}

/** Il dato obbligatorio manca? Si evidenzia dove compare nella linguetta Anagrafica. */
export const mancaObbligatorio = (d: DatiMancanti | undefined, ...codici: string[]) =>
  codici.some((c) => d?.obbligatori.includes(c) ?? false)
